import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import { getRequest } from '../../utils/axiosUtils';
import DailyDropoff from './DailyDropoff';

/**
 * Onboarding Drop-off (Ghost Funnel) — a full page (route `/onboarding-dropoff`),
 * opened from the "Ghost Users" KPI card on the Channel Attribution page. It renders
 * inside the admin Layout (sidebar + header) exactly like Channel Attribution and the
 * attribution-members page — not as a modal/overlay. The current period/gender/platform
 * ride in from the CA page as query params so it opens on the same view.
 *
 * Data: GET /admin/attribution/ghost-funnel?from&to&platform returns, over ALL sign-ups
 * in the window, the per-step furthest-reached distribution split by gender (dist.all /
 * women / men), canonical KPIs (kpisByGender), and the step catalogue. The drop-off IS
 * the ghost story. All computation here (reached = cumulative dist, biggest leak, gender
 * split) mirrors the reference design but runs on live data.
 *
 * Scoped under `.favor-gf` so its styles never touch the admin's global CSS. Fonts use
 * the admin app's own families (ivypresto-headline serif + indivisible sans).
 */

const iso = (d) => d.toISOString().slice(0, 10);
const TODAY = iso(new Date());
const shiftDays = (isoStr, n) => { const d = new Date(isoStr); d.setDate(d.getDate() + n); return iso(d); };

// HARD CUTOFF — the day both clients shipped the reordered signup flow. Sign-ups before it
// are excluded server-side (AttributionController.GHOST_FUNNEL_CUTOFF) and the calendar is
// bounded to [CUTOFF, TODAY] so no earlier date is selectable. MUST equal the server
// constant: set it earlier and the page offers days the server will refuse to count, set it
// later and days the server has are unreachable.
//
// Moved here from the 2026-07-08 taxonomy launch on 2026-08-27. The server can place the
// older cohort correctly, but the old flow asked Intent / Work field / Relationship status /
// Body type LATE where the current taxonomy numbers them EARLY — so those rows read as
// near-zero drop for pre-reorder sign-ups and their real losses pile into the photo and
// About rows. One chart cannot honestly carry both. The 2026-09-01 removal of the
// Email / OTP / Password rows did NOT need the cutoff moved again: those screens went with
// the Google-only switch on 2026-08-26, after it.
// ISO date strings compare correctly with </>, so clamp is trivial.
const CUTOFF = '2026-08-25';
const clampDay = (d) => (d < CUTOFF ? CUTOFF : d > TODAY ? TODAY : d);
const CUTOFF_LABEL = (() => {
  const [y, m, d] = CUTOFF.split('-');
  return `${+d} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m - 1]} ${y}`;
})();

// period -> {from,to}. 'campaign' (since launch) leaves from open.
function rangeWindow(range, from, to) {
  switch (range) {
    case 'yesterday': { const y = shiftDays(TODAY, -1); return { from: y, to: y }; }
    case '30d': return { from: shiftDays(TODAY, -29), to: TODAY };
    case 'campaign': return { from: undefined, to: TODAY };
    case 'custom': return { from, to };
    case '7d':
    default: return { from: shiftDays(TODAY, -6), to: TODAY };
  }
}

// Index of the "Gender" step — the base the gender split is measured from (you cannot
// split by a gender the member has not picked yet). Found by KEY off the catalogue the
// server sends, not hardcoded: the taxonomy has been renumbered twice (2026-08-26, and
// again on 2026-09-01 when Email/OTP/Password collapsed into one Account created row) and a
// literal index would have silently pointed at a different step both times. 1 is the
// last-resort fallback for a stale backend that sends no keys.
const genderStepIdx = (steps) => {
  const i = (steps || []).findIndex((s) => s?.key === 'gender');
  return i >= 0 ? i : 1;
};
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const pctOf = (a, b) => (b ? (a / b) * 100 : 0);

// reached[i] = users who got at least to step i = sum of dist[j>=i]
const toReached = (dist) => dist.map((_, i) => dist.slice(i).reduce((a, b) => a + b, 0));

function biggestLeak(counts, from) {
  from = from || 1; let idx = from, max = -1;
  for (let i = from; i < counts.length; i++) { const dr = counts[i - 1] - counts[i]; if (dr > max) { max = dr; idx = i; } }
  const base = counts[0] || 1;
  return { step: idx, lost: Math.max(max, 0), pct: Math.round(Math.max(max, 0) / base * 100) };
}

const PERIODS = [['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['campaign', 'Since the reorder'], ['custom', 'Custom']];
const GENDERS = [['all', 'All'], ['women', 'Women'], ['men', 'Men']];
const PLATFORMS = [['all', 'All'], ['app', 'App'], ['web', 'Web']];
const GVIEWS = [['both', 'Both'], ['women', 'Women'], ['men', 'Men']];
// Which population the drop-off is drawn over. GHOST is the default because this page
// opens from the "Ghost Users" card, and a drill-through has to show the people that were
// clicked: on that cohort the funnel entry, every row's "users lost" and the Ghost KPI
// are one and the same number. ALL is the wider view — every sign-up, including members
// who have since deleted or deactivated — and its total is deliberately NOT the card's.
const COHORTS = [['ghost', 'Ghost users'], ['all', 'All sign-ups']];

const Pills = ({ items, value, onChange, variant }) => (
  <div className="gf-pills">
    {items.map(([k, label]) => (
      <button key={k} type="button" className={`gf-pill${value === k ? ` on ${variant || ''}` : ''}`} onClick={() => onChange(k)}>{label}</button>
    ))}
  </div>
);

const GhostFunnel = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // Initial view carried over from the Channel Attribution page (all optional).
  const initRange = sp.get('range');
  const initGender = sp.get('gender');
  const initPlatform = sp.get('platform');

  const [range, setRange] = useState(['yesterday', '7d', '30d', 'campaign', 'custom'].includes(initRange) ? initRange : '7d');
  const [from, setFrom] = useState(clampDay(sp.get('from') || CUTOFF));
  const [to, setTo] = useState(clampDay(sp.get('to') || TODAY));
  const [gender, setGender] = useState(['women', 'men'].includes(initGender) ? initGender : 'all');
  const [platform, setPlatform] = useState(['app', 'web'].includes(initPlatform) ? initPlatform : 'all');
  const [genderView, setGenderView] = useState('both');
  const [cohort, setCohort] = useState('ghost');
  // Funnel (default) vs the daily drop-off Graph View. The daily matrix is fetched
  // lazily the first time Graph View is opened (and re-fetched when the period/platform
  // changes while it's open) — the funnel load never pays for it.
  const [view, setView] = useState('funnel');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  const [daily, setDaily] = useState(null);
  const [dailyKey, setDailyKey] = useState(null);   // window signature the daily data is for
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    (async () => {
      try {
        const win = rangeWindow(range, from, to);
        const params = {};
        if (win.from) params.from = win.from;
        if (win.to) params.to = win.to;
        if (platform !== 'all') params.platform = platform;
        params.cohort = cohort;
        const res = await getRequest({ url: '/admin/attribution/ghost-funnel', params });
        if (!active) return;
        // getRequest returns null when the call was aborted (a superseding request to the
        // same URL path). A cancel is NOT a failure — ignore it so we don't flash an error.
        if (!res) return;
        const body = res?.data;
        const payload = body?.dist ? body : body?.data;
        if (payload && payload.dist && Array.isArray(payload.steps)) { setData(payload); }
        else setError(true);
      } catch (e) { if (active) setError(true); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [range, from, to, platform, cohort]);

  // Lazy per-day matrix for the Graph View. Fetched only when Graph View is open and the
  // current window (period/platform) differs from what we already hold — gender is a
  // client-side slice (dist carries all/women/men) so it never triggers a re-fetch.
  useEffect(() => {
    if (view !== 'graph') return undefined;
    const win = rangeWindow(range, from, to);
    const key = `${win.from || ''}|${win.to || ''}|${platform}|${cohort}`;
    if (dailyKey === key) return undefined; // already have this window
    let active = true;
    setDailyLoading(true); setDailyError(false);
    (async () => {
      try {
        const params = { daily: 1 };
        if (win.from) params.from = win.from;
        if (win.to) params.to = win.to;
        if (platform !== 'all') params.platform = platform;
        params.cohort = cohort;
        // isAbort:false — the funnel and daily calls hit the SAME URL path, and getRequest
        // aborts by path (ignoring query). Without this, the daily fetch would cancel the
        // in-flight funnel fetch (and vice versa), surfacing as "Couldn't load the funnel
        // data". Staleness is already handled by the `active` flag + `dailyKey` guard.
        const res = await getRequest({ url: '/admin/attribution/ghost-funnel', params, isAbort: false });
        if (!active) return;
        if (!res) return; // aborted — not a failure
        const body = res?.data;
        const payload = Array.isArray(body?.daily) ? body : body?.data;
        if (payload && Array.isArray(payload.daily)) { setDaily(payload.daily); setDailyKey(key); }
        else setDailyError(true);
      } catch (e) { if (active) setDailyError(true); }
      finally { if (active) setDailyLoading(false); }
    })();
    return () => { active = false; };
  }, [view, range, from, to, platform, cohort, dailyKey]);

  const steps = data?.steps || [];
  const genderKey = gender === 'all' ? 'all' : gender;
  const counts = useMemo(() => (data ? toReached(data.dist[genderKey] || data.dist.all) : []), [data, genderKey]);
  const kpis = data ? (data.kpisByGender?.[genderKey] || data.kpis) : null;
  // "Visible now" = Approved only. An older server build has no `approved` key, and its
  // `active` meant the same visible-now count, so fall back to it.
  const visibleNow = kpis ? (kpis.approved != null ? kpis.approved : kpis.active) || 0 : 0;

  const periodLabel = range === 'custom' ? `${from} → ${to}` : { yesterday: 'Yesterday', '7d': 'Last 7 days', '30d': 'Last 30 days', campaign: 'Since the reorder' }[range];
  const genderLabel = { all: 'all genders', women: 'women', men: 'men' }[gender];
  const platformLabel = { all: 'all platforms', app: 'app', web: 'web' }[platform];

  const leak = counts.length ? biggestLeak(counts, 1) : { step: 0, lost: 0, pct: 0 };
  const ghostCohort = cohort === 'ghost';
  const signups = counts[0] || 0;
  // The LAST entry is the funnel TERMINAL ("Profile complete"), which is not a screen —
  // the last screen anybody navigates to is the one before it. Conflating the two is what
  // made the old callout compare the last screen against the wrong figure.
  const reachedLastScreen = counts.length > 1 ? counts[counts.length - 2] : 0;
  const completedProfile = counts[counts.length - 1] || 0;
  // Named off the catalogue, never spelled out: the taxonomy has been renumbered and had its
  // last screen renamed once already, and a hardcoded "selfie review" survived both.
  const lastScreenLabel = (steps[steps.length - 2]?.label || 'the last screen').toLowerCase();

  // Export the per-step drop-off as CSV — reached (cumulative) counts for the whole
  // cohort plus the women/men split, so the funnel is auditable outside the dashboard.
  // Period/platform are already applied server-side (they shape `data`); the gender
  // columns come straight from dist so one file covers all three views.
  const exportCsv = () => {
    if (!data) return;
    const rAll = toReached(data.dist.all || []);
    const rW = toReached(data.dist.women || []);
    const rM = toReached(data.dist.men || []);
    const base = rAll[0] || 1;
    const head = ['Step', 'Label', 'Reached (All)', '% of sign-ups', 'Dropped from prev', 'Reached (Women)', 'Reached (Men)'];
    const rows = steps.map((s, i) => {
      const pctv = Math.round(((rAll[i] || 0) / base) * 100);
      return [s.step, s.label, rAll[i] || 0, `${pctv}%`, i > 0 ? (rAll[i - 1] || 0) - (rAll[i] || 0) : '', rW[i] || 0, rM[i] || 0];
    });
    const csv = [head, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding-dropoff-${cohort}-${range}-${TODAY}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ pb: 4 }}>
      <style>{STYLES}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <HeadTitle
          imageSrc="/assets/images/admin-panel/analytics.png"
          text="Onboarding Drop-off"
          description="Where sign-ups turn into ghosts — by step, split by gender"
        />
        <button type="button" className="gf-back" onClick={() => navigate('/channel-attribution')}>
          ← Channel Attribution
        </button>
      </Stack>

      <div className="favor-gf">
          <div className="filters">
            <div className="fg"><span className="lab">Period</span>
              <Pills items={PERIODS} value={range} onChange={setRange} variant="red" />
              {range === 'custom' && (
                <span className="daterange show">
                  <input type="date" min={CUTOFF} max={TODAY} value={from} onChange={(e) => { const v = clampDay(e.target.value); setFrom(v); if (v > to) setTo(v); }} />
                  <span className="dr-sep">to</span>
                  <input type="date" min={CUTOFF} max={TODAY} value={to} onChange={(e) => { const v = clampDay(e.target.value); setTo(v); if (v < from) setFrom(v); }} />
                </span>
              )}
            </div>
            <div className="fg"><span className="lab">Gender</span>
              <Pills items={GENDERS} value={gender} onChange={setGender} variant="black" />
            </div>
            <div className="fg"><span className="lab">Platform</span>
              <Pills items={PLATFORMS} value={platform} onChange={setPlatform} variant="black" />
            </div>
            <div className="fg"><span className="lab">Cohort</span>
              <Pills items={COHORTS} value={cohort} onChange={setCohort} variant="black" />
            </div>
            <div className="fg"><span className="lab">View</span>
              <Pills items={[['funnel', 'Funnel'], ['graph', 'Graph View']]} value={view} onChange={setView} variant="red" />
            </div>
            <button type="button" className="gf-export" onClick={exportCsv} disabled={!data}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M8 2v8m0 0L5 7m3 3l3-3M3 12h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Export
            </button>
          </div>

          <p className="note">Showing: <b>{periodLabel} · {genderLabel} · {platformLabel}</b> · step counts are distinct users who reached each screen.
            {' '}{ghostCohort
              ? <b>Drawn over the Ghost Users from Channel Attribution — the rows add up to that card exactly.</b>
              : <b>Drawn over every sign-up, including members who have since deleted or deactivated — so the total is larger than the Ghost Users card.</b>}
            <span className="flag">◆ App/web = install-token heuristic · Sign-ups from {CUTOFF_LABEL} only — the day the signup flow was reordered. Earlier sign-ups walked a different order of screens and are not comparable, so they are excluded rather than blended in.</span></p>

          {loading ? (
            <div style={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader /></div>
          ) : error || !data ? (
            <div className="note" style={{ padding: '40px 0' }}>Couldn’t load the funnel data. Try another period.</div>
          ) : view === 'graph' ? (
            dailyLoading && !daily ? (
              <div style={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader /></div>
            ) : dailyError && !daily ? (
              <div className="note" style={{ padding: '40px 0' }}>Couldn’t load the daily drop-off. Try another period, or reload the backend if the daily feed hasn’t shipped yet.</div>
            ) : (
              <DailyDropoff
                daily={daily || []}
                steps={steps}
                genderKey={genderKey}
                // Gender is a client-side slice of dist, but the cell drill-down asks the
                // server for the actual users — so it needs BOTH filters to select the same
                // people the cell counted.
                platform={platform}
                windowFrom={rangeWindow(range, from, to).from || CUTOFF}
                windowTo={rangeWindow(range, from, to).to || TODAY}
                canonicalGhost={kpis ? kpis.ghost : undefined}
                cohort={cohort}
              />
            )
          ) : (
            <>
              {/* KPIs */}
              <div className="kpis">
                <Kpi k="Sign-ups" v={fmt(kpis.signups)} d="reached the email step" />
                {/* Completed = finished ONBOARDING (100% + approved selfie + a photo), whatever
                    state the account is in now. It is NOT `approved`: a member the curation cron
                    hid still finished. Reading the visible count here reported a 2.3% completion
                    rate on a real 23%, so the two are separate tiles now. Visible now = Approved
                    only (kpis.approved; kpis.active from an older server build). */}
                <Kpi k="Completed" v={fmt(kpis.completed)} d={`${Math.round(kpis.completionRate)}% of sign-ups`} cls="g" />
                <Kpi k="Ghost" v={fmt(kpis.ghost)} d={`${Math.round(pctOf(kpis.ghost, kpis.signups))}% never finished`} cls="a" />
                <Kpi k="Visible now" v={fmt(visibleNow)} d={`${fmt(Math.max(kpis.completed - visibleNow, 0))} completed but not visible`} cls="g" />
                <Kpi k="Biggest leak" v={`–${leak.pct}%`} d={`at ${steps[leak.step]?.label || '—'}`} cls="r" />
              </div>

              {/* OVERALL FUNNEL */}
              <div className="sec">
                <div className="sec-h">
                  <h2>The overall funnel</h2>
                  <span className="hint">Bars show the share of sign-ups still present at each step. The gap is drop-off.</span>
                </div>
                <div className="funnel">
                  {counts.map((c, i) => {
                    const pct = pctOf(c, signups);
                    let dropTxt = 'entry'; let dropBig = '';
                    if (i > 0) { const lost = counts[i - 1] - c; const lpct = signups ? Math.round(lost / signups * 100) : 0; dropTxt = `–${fmt(lost)} · –${lpct}%`; if (i === leak.step) dropBig = 'big'; }
                    const isLeak = i === leak.step ? 'leak' : '';
                    const done = i === counts.length - 1 ? 'done' : '';
                    const thin = pct < 14 ? 'thin' : '';
                    return (
                      <div className={`frow ${isLeak}`} key={i}>
                        <div className="fname"><span className="snum">{i + 1}</span>{steps[i]?.label}</div>
                        <div className="ftrack"><div className={`fbar ${done} ${thin}`} style={{ width: `${Math.max(pct, 0.6)}%` }}><span className="pct">{pct.toFixed(0)}%</span></div></div>
                        <div className="fmeta"><div className="cnt">{fmt(c)}</div><div className={`drop ${dropBig}`}>{dropTxt}</div></div>
                      </div>
                    );
                  })}
                </div>
                <div className="ceta">
                  {/* Named for the step they never reached, like the Biggest leak KPI above and
                      the women/men callouts below — all four describe the same kind of gap. */}
                  <Callout t="Biggest single leak" cls="leak"><b>{steps[leak.step]?.label}</b> — <b>{fmt(leak.lost)}</b> users ({leak.pct}% of sign-ups) never reached it, the most lost at any one step.</Callout>
                  {ghostCohort ? (
                    <Callout t="The last-mile ghosts" cls="leak"><b>{fmt(reachedLastScreen)}</b> ghosts reached {lastScreenLabel} and still never completed — a rejected selfie, or a profile left under 100%. Everyone else gave up earlier.</Callout>
                  ) : (
                    <Callout t="Reached the last screen vs completed" cls="leak"><b>{fmt(reachedLastScreen)}</b> reached {lastScreenLabel} and <b>{fmt(completedProfile)}</b> went on to complete a profile. Of those, <b>{fmt(visibleNow)}</b> are approved and visible now — the rest are hidden (curation or admin), inactive, deactivated, suspended or deleted.</Callout>
                  )}
                  <Callout t="Where most ghosts sit">Of {fmt(kpis.signups)} sign-ups, <b>{fmt(kpis.ghost)}</b> never reach a complete profile{ghostCohort ? ' — and every row above is one of them' : ''}.</Callout>
                </div>
              </div>

              {/* GENDER SPLIT */}
              <div className="sec">
                <div className="sec-h">
                  <h2>Where women &amp; men fall off</h2>
                  <span className="hint">Measured from the Gender step (= 100%). Toggle to compare or view one segment.</span>
                </div>
                <Pills items={GVIEWS} value={genderView} onChange={setGenderView} variant="black" />
                <GenderSplit data={data} steps={steps} genderView={genderView} />
              </div>
            </>
          )}
      </div>
    </Box>
  );
};

const Kpi = ({ k, v, d, cls, unit }) => (
  <div className={`kpi ${cls || ''}`}><div className="k">{k}</div><div className="v serif">{v}{unit ? <small>{unit}</small> : null}</div><div className="d">{d}</div></div>
);
const Callout = ({ t, cls, children }) => (
  <div className={`callout ${cls || ''}`}><div className="ct">{t}</div><div className="cv">{children}</div></div>
);

function GenderSplit({ data, steps, genderView }) {
  const GENDER_STEP_IDX = genderStepIdx(steps);
  const w = useMemo(() => toReached(data.dist.women), [data]);
  const m = useMemo(() => toReached(data.dist.men), [data]);
  const wb = w[GENDER_STEP_IDX] || 0, mb = m[GENDER_STEP_IDX] || 0;
  const wLeak = biggestLeak(w, GENDER_STEP_IDX + 1), mLeak = biggestLeak(m, GENDER_STEP_IDX + 1);

  // widest gap step
  let gapIdx = GENDER_STEP_IDX + 1, gapMax = -1;
  for (let i = GENDER_STEP_IDX + 1; i < steps.length; i++) {
    const g = Math.abs(pctOf(w[i], wb) - pctOf(m[i], mb));
    if (g > gapMax) { gapMax = g; gapIdx = i; }
  }

  if (genderView !== 'both') {
    const g = genderView; const c = g === 'women' ? w : m; const col = g === 'women' ? 'w' : 'm';
    const base = c[GENDER_STEP_IDX] || 0, end = c[steps.length - 1] || 0;
    const leak = biggestLeak(c, GENDER_STEP_IDX + 1);
    const nm = g === 'women' ? 'Women' : 'Men';
    return (
      <>
        <div className="cmp-legend"><span className="lg" style={{ color: 'var(--faint)' }}>{nm} · base {fmt(base)} at Gender step · {fmt(end)} complete ({base ? Math.round(end / base * 100) : 0}%)</span></div>
        <div className="cmp">
          {steps.map((s, i) => {
            if (i < GENDER_STEP_IDX) return null;
            const pct = pctOf(c[i], base);
            let meta = <span className="cd">base</span>;
            if (i > GENDER_STEP_IDX) { const lost = c[i - 1] - c[i]; const lp = base ? Math.round(lost / base * 100) : 0; meta = <span className={`cd${i === leak.step ? ' big' : ''}`}>−{fmt(lost)} · −{lp}%</span>; }
            const cls = ['solo']; if (i === leak.step) cls.push('leakrow'); if (i === steps.length - 1) cls.push('done');
            return (
              <div className={`crow ${cls.join(' ')}`} key={i}>
                <div className="cn"><span className="snum">{i + 1}</span><span className="cnm">{s.label}</span></div>
                <div className="cbars"><div className={`cbar ${col}`}><div className="ctrack"><div className="cfill" style={{ width: `${Math.max(pct, 0.6)}%` }} /></div><span className="cp">{pct.toFixed(0)}%</span><span className="cc">{fmt(c[i])}</span>{meta}</div></div>
              </div>
            );
          })}
        </div>
        <div className="ceta">
          <Callout t={`${nm} — biggest leak`} cls="leak"><b>{steps[leak.step]?.label}</b> — <b>{fmt(leak.lost)}</b> {nm.toLowerCase()} ({leak.pct}%) never reached it, the most lost at any one step.</Callout>
          <Callout t={`${nm} — completion`}>Of <b>{fmt(base)}</b> {nm.toLowerCase()} at the Gender step, <b>{fmt(end)}</b> finish — a {base ? Math.round(end / base * 100) : 0}% completion rate.</Callout>
        </div>
      </>
    );
  }

  // both (women vs men head-to-head)
  const wpG = wb ? Math.round(w[gapIdx] / wb * 100) : 0, mpG = mb ? Math.round(m[gapIdx] / mb * 100) : 0;
  return (
    <>
      <div className="cmp-legend">
        <span className="lg"><span className="dotw" />Women</span>
        <span className="lg"><span className="dotm" />Men</span>
        <span className="lg" style={{ color: 'var(--faint)' }}>Base: {fmt(wb)} women · {fmt(mb)} men at Gender step</span>
      </div>
      <div className="cmp">
        {steps.map((s, i) => {
          if (i < GENDER_STEP_IDX) return null;
          const wp = pctOf(w[i], wb), mp = pctOf(m[i], mb);
          const gp = wp - mp, dir = gp >= 0 ? 'w' : 'm', ag = Math.abs(gp);
          const cls = []; if (i === gapIdx) cls.push('gapbig'); if (i === steps.length - 1) cls.push('done');
          const line = (kind, arr, base, leakStep) => {
            const pct = pctOf(arr[i], base);
            let meta = <span className="cd">base</span>;
            if (i > GENDER_STEP_IDX) { const lost = arr[i - 1] - arr[i]; const lp = base ? Math.round(lost / base * 100) : 0; meta = <span className={`cd${i === leakStep ? ' big' : ''}`}>−{fmt(lost)} · −{lp}%</span>; }
            return <div className={`cbar ${kind}`}><div className="ctrack"><div className="cfill" style={{ width: `${Math.max(pct, 0.6)}%` }} /></div><span className="cp">{pct.toFixed(0)}%</span><span className="cc">{fmt(arr[i])}</span>{meta}</div>;
          };
          return (
            <div className={`crow ${cls.join(' ')}`} key={i}>
              <div className="cn"><span className="snum">{i + 1}</span><span className="cnm">{s.label}</span>
                {i === wLeak.step ? <span className="mk w">W▼</span> : null}{i === mLeak.step ? <span className="mk m">M▼</span> : null}
              </div>
              <div className="cbars">{line('w', w, wb, wLeak.step)}{line('m', m, mb, mLeak.step)}</div>
              <div className={`cgap ${dir}${ag >= 8 ? ' strong' : ''}`}>{ag < 0.5 ? 'even' : `Δ${ag.toFixed(0)}% ${dir === 'w' ? 'W' : 'M'}`}</div>
            </div>
          );
        })}
      </div>
      <div className="ceta">
        <Callout t="Women fall most at" cls="leak"><b>{steps[wLeak.step]?.label}</b> — losing <b>{fmt(wLeak.lost)}</b> women ({wLeak.pct}%) in one step.</Callout>
        <Callout t="Men fall most at" cls="leak"><b>{steps[mLeak.step]?.label}</b> — losing <b>{fmt(mLeak.lost)}</b> men ({mLeak.pct}%) in one step.</Callout>
        <Callout t="Widest gender gap">At <b>{steps[gapIdx]?.label}</b>: {wpG}% of women present vs {mpG}% of men — {wpG >= mpG ? 'men' : 'women'} drop out faster here.</Callout>
      </div>
    </>
  );
}

const STYLES = `
.gf-back{border:1px solid #e3e3e3;background:#fff;font-family:"indivisible","Helvetica Neue",Arial,sans-serif;font-size:13px;font-weight:600;color:#3f4854;padding:9px 15px;border-radius:10px;cursor:pointer;white-space:nowrap;flex-shrink:0;transition:.12s}
.gf-back:hover{background:#f4f2ef;color:#16161a}
.favor-gf{--ink:#25313f;--ink-2:#3f4854;--muted:#7c8590;--faint:#9aa2ac;--line:#ececec;--line-2:#e3e3e3;--wash:#f4f2ef;--red:#e15241;--red-d:#d43f2d;--green:#2f8f5b;--gold:#c08a2e;--gold-b:#d9a441;--blue:#3f6fd8;
  --serif:"ivypresto-headline",Georgia,serif;--sans:"indivisible","Neue Montreal","Helvetica Neue",Arial,sans-serif;
  --card-sh:0 1px 2px rgba(20,25,35,.04),0 6px 18px -10px rgba(20,25,35,.10);
  color:var(--ink-2);font-family:var(--sans);font-size:14px;line-height:1.5;-webkit-font-smoothing:antialiased}
.favor-gf *{box-sizing:border-box;margin:0;padding:0}
.favor-gf .serif{font-family:var(--serif)}
.favor-gf .filters{display:flex;flex-wrap:wrap;gap:20px 26px;align-items:center;margin:8px 0 4px}
.favor-gf .fg{display:flex;align-items:center;gap:12px}
.favor-gf .fg>.lab{font-size:11px;font-weight:700;letter-spacing:.12em;color:var(--faint);text-transform:uppercase}
.favor-gf .gf-export{margin-left:auto;display:inline-flex;align-items:center;gap:7px;background:#14181c;border:0;border-radius:10px;padding:9px 15px;font-family:inherit;font-size:12.5px;font-weight:600;color:#fff;cursor:pointer;transition:.12s}
.favor-gf .gf-export:hover{background:#000}
.favor-gf .gf-export:disabled{opacity:.45;cursor:default}
.favor-gf .gf-export svg{display:block}
.favor-gf .gf-pills{display:inline-flex;background:var(--wash);border:1px solid var(--line);border-radius:11px;padding:4px}
.favor-gf .gf-pill{border:0;background:transparent;font-family:inherit;font-size:12.5px;font-weight:600;color:var(--ink-2);padding:7px 13px;border-radius:8px;cursor:pointer;white-space:nowrap;transition:.12s}
.favor-gf .gf-pill:hover{color:var(--ink)}
.favor-gf .gf-pill.on{background:#fff;color:var(--ink);box-shadow:0 1px 3px rgba(0,0,0,.12)}
.favor-gf .gf-pill.on.red{background:var(--red);color:#fff;box-shadow:0 2px 8px -2px rgba(225,82,65,.5)}
.favor-gf .gf-pill.on.black{background:#14181c;color:#fff}
.favor-gf .note{font-size:12px;color:var(--muted);line-height:1.5;margin:14px 0 4px;font-style:italic}
.favor-gf .note b{color:var(--ink-2);font-style:normal}
.favor-gf .flag{display:inline-flex;gap:7px;align-items:center;font-style:normal;font-size:11px;font-weight:600;color:#8a6d1f;background:#fdf6e6;border:1px solid #f0e2bd;padding:3px 9px;border-radius:7px;margin-left:4px}
.favor-gf .daterange{display:inline-flex;align-items:center;gap:8px}
.favor-gf .daterange input[type=date]{font-family:inherit;font-size:12.5px;font-weight:600;color:var(--ink-2);border:1px solid var(--line-2);border-radius:10px;padding:8px 11px;background:#fff;cursor:pointer}
.favor-gf .dr-sep{font-size:12px;color:var(--muted)}
.favor-gf .kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:13px;margin:18px 0 24px}
.favor-gf .kpi{border:1px solid var(--line);border-radius:14px;padding:15px 17px;box-shadow:var(--card-sh);background:#fff}
.favor-gf .kpi .k{font-size:10.5px;font-weight:700;letter-spacing:.05em;color:var(--faint);text-transform:uppercase;margin-bottom:8px}
.favor-gf .kpi .v{font-weight:700;font-size:19px;line-height:1;color:var(--ink);letter-spacing:-.01em}
.favor-gf .kpi .v small{font-family:var(--sans);font-size:11.5px;font-weight:700;margin-left:5px}
.favor-gf .kpi .d{font-size:11.5px;color:var(--muted);margin-top:6px}
.favor-gf .kpi.g{background:linear-gradient(180deg,#f2faf5,#fff);border-color:#dcece2}.favor-gf .kpi.g .v{color:var(--green)}
.favor-gf .kpi.a{background:linear-gradient(180deg,#fdf7e9,#fff);border-color:#f0e4c6}.favor-gf .kpi.a .v{color:var(--gold)}
.favor-gf .kpi.r{background:linear-gradient(180deg,#fdf2f0,#fff);border-color:#f7dad4}.favor-gf .kpi.r .v{color:var(--red-d)}
.favor-gf .sec{border:1px solid var(--line);border-radius:16px;padding:20px 22px;box-shadow:var(--card-sh);margin-bottom:18px}
.favor-gf .sec-h{display:flex;align-items:baseline;justify-content:space-between;gap:16px;margin-bottom:6px;flex-wrap:wrap}
.favor-gf .sec-h h2{font-family:var(--sans);font-weight:600;font-size:24px !important;line-height:1.3;color:var(--ink)}
.favor-gf .sec-h .hint{font-size:12px;color:var(--muted);font-style:italic}
.favor-gf .funnel{margin-top:14px}
.favor-gf .frow{display:grid;grid-template-columns:220px 1fr 150px;align-items:center;gap:16px;padding:4px 0}
.favor-gf .frow .fname{font-size:13px;font-weight:500;color:var(--ink-2);display:flex;align-items:center;gap:9px}
.favor-gf .frow .snum{width:20px;height:20px;border-radius:6px;background:var(--wash);color:var(--muted);font-size:10.5px;font-weight:700;display:grid;place-items:center;flex-shrink:0}
.favor-gf .ftrack{position:relative;height:30px;background:#f5f3f0;border-radius:8px;overflow:hidden}
.favor-gf .fbar{position:absolute;inset:0 auto 0 0;border-radius:8px;background:linear-gradient(90deg,#eecb7d,#dba53f);transition:width .5s cubic-bezier(.4,0,.2,1);min-width:3px}
.favor-gf .fbar.done{background:linear-gradient(90deg,#5cb98a,#2f8f5b)}
.favor-gf .fbar .pct{position:absolute;right:11px;top:50%;transform:translateY(-50%);font-size:11px;font-weight:700;color:#4a3a12}
.favor-gf .fbar.done .pct{color:#0f3d28}
.favor-gf .fbar.thin .pct{right:-42px;color:var(--muted)}
.favor-gf .fmeta{text-align:right}
.favor-gf .fmeta .cnt{font-family:var(--serif);font-weight:700;font-size:15px;color:var(--ink)}
.favor-gf .fmeta .drop{font-size:11px;font-weight:600;color:var(--muted);margin-top:1px}
.favor-gf .fmeta .drop.big{color:var(--red-d)}
.favor-gf .frow.leak .fname{font-weight:700;color:var(--ink)}
.favor-gf .frow.leak .snum{background:#fdeae6;color:var(--red-d)}
.favor-gf .frow.leak .ftrack{box-shadow:0 0 0 2px #f6cabf}
.favor-gf .ceta{display:flex;gap:12px;flex-wrap:wrap;margin-top:16px}
.favor-gf .callout{flex:1;min-width:230px;border:1px solid var(--line);border-radius:12px;padding:13px 16px;background:#fafafa}
.favor-gf .callout .ct{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);margin-bottom:6px}
.favor-gf .callout .cv{font-size:12.5px;color:var(--ink-2);line-height:1.5}
.favor-gf .callout .cv b{color:var(--ink)}
.favor-gf .callout.leak{background:#fdf3f1;border-color:#f7d9d2}.favor-gf .callout.leak .cv b{color:var(--red-d)}
.favor-gf .cmp-legend{display:flex;gap:18px;align-items:center;flex-wrap:wrap;font-size:12px;color:var(--muted);margin-top:14px;margin-bottom:4px}
.favor-gf .cmp-legend .lg{display:inline-flex;align-items:center;gap:7px;font-weight:600}
.favor-gf .cmp-legend .dotw{width:12px;height:12px;border-radius:4px;background:linear-gradient(90deg,#e79ac0,#d76aa8)}
.favor-gf .cmp-legend .dotm{width:12px;height:12px;border-radius:4px;background:linear-gradient(90deg,#7ea8e6,#3f6fd8)}
.favor-gf .cmp{margin-top:8px}
.favor-gf .crow{display:grid;grid-template-columns:180px 1fr 66px;align-items:center;gap:16px;padding:8px 12px;border-radius:12px}
.favor-gf .crow+.crow{margin-top:3px}
.favor-gf .crow.gapbig{background:#fbf6ec;box-shadow:inset 3px 0 0 var(--gold-b)}
.favor-gf .crow.solo{grid-template-columns:220px 1fr}
.favor-gf .crow.solo .cbar .ctrack{height:22px}
.favor-gf .crow.leakrow{background:#fdf5f3;box-shadow:inset 3px 0 0 var(--red-d)}
.favor-gf .cn{display:flex;align-items:center;gap:8px;min-width:0}
.favor-gf .cn .snum{width:20px;height:20px;border-radius:6px;background:var(--wash);color:var(--muted);font-size:10.5px;font-weight:700;display:grid;place-items:center;flex-shrink:0}
.favor-gf .cn .cnm{font-size:12.5px;font-weight:500;color:var(--ink-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.favor-gf .cn .mk{font-size:9.5px;font-weight:800;padding:2px 5px;border-radius:5px;flex-shrink:0}
.favor-gf .cn .mk.w{color:#b23c7e;background:#fbeef4}.favor-gf .cn .mk.m{color:#2d5bb8;background:#eef3fc}
.favor-gf .cbars{display:flex;flex-direction:column;gap:6px}
.favor-gf .cbar{display:grid;grid-template-columns:1fr 32px 50px 92px;align-items:center;gap:9px}
.favor-gf .cbar .ctrack{position:relative;height:16px;background:#f1efeb;border-radius:5px;overflow:hidden}
.favor-gf .cbar .cfill{position:absolute;inset:0 auto 0 0;border-radius:5px;min-width:2px;transition:width .5s cubic-bezier(.4,0,.2,1)}
.favor-gf .cbar.w .cfill{background:linear-gradient(90deg,#e79ac0,#d76aa8)}
.favor-gf .cbar.m .cfill{background:linear-gradient(90deg,#7ea8e6,#3f6fd8)}
.favor-gf .crow.done .cbar .cfill{background:linear-gradient(90deg,#5cb98a,#2f8f5b)}
.favor-gf .cbar .cp{font-size:11px;font-weight:700;color:var(--ink-2);text-align:right}
.favor-gf .cbar .cc{font-size:12px;font-weight:700;color:var(--ink);text-align:right}
.favor-gf .cbar .cd{font-size:10px;color:var(--faint);text-align:right;white-space:nowrap}
.favor-gf .cbar .cd.big{color:var(--red-d);font-weight:700}
.favor-gf .cgap{font-size:11px;font-weight:700;text-align:center;color:var(--muted);border-radius:8px;padding:6px 4px;background:#f4f2ef}
.favor-gf .cgap.w{color:#b23c7e;background:#fbeef4}.favor-gf .cgap.m{color:#2d5bb8;background:#eef3fc}
.favor-gf .cgap.strong{box-shadow:inset 0 0 0 1.4px currentColor}
@media(max-width:820px){.favor-gf .kpis{grid-template-columns:repeat(2,1fr)}.favor-gf .frow{grid-template-columns:150px 1fr 96px}.favor-gf .crow{grid-template-columns:110px 1fr 52px;gap:10px}.favor-gf .cbar{grid-template-columns:1fr 28px 74px}.favor-gf .cbar .cc{display:none}}
`;

export default GhostFunnel;
